import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useColorScheme } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { UnistylesRuntime } from 'react-native-unistyles';
import { asHexColor } from '../services/colors';

interface ThemeContextValue {
    themePreference: ThemePreference;
    toggleTheme: () => Promise<void>;
}

const themePreferences: ThemePreference[] = ['light', 'lightAlt', 'dark', 'automatic'];

const defaultValue: ThemeContextValue = {
    themePreference: 'automatic',
    toggleTheme: async () => { }
};

const ThemeContext = createContext(defaultValue);

function getThemeForPreference(preference: ThemePreference, colorScheme: ReturnType<typeof useColorScheme>): Theme {
    if (preference !== 'automatic') {
        return preference;
    }

    if (colorScheme === 'dark') {
        return 'dark';
    }

    if (colorScheme === 'light') {
        return 'lightAlt';
    }

    // Preserve the existing fallback when the system color scheme is unavailable.
    return 'light';
}

export const ThemeProvider = ({ children }: { children: React.ReactNode | undefined; }) => {
    const colorScheme = useColorScheme();
    const [themePreference, setThemePreference] = useState<ThemePreference>('automatic');
    const [isPreferenceLoaded, setIsPreferenceLoaded] = useState(false);
    const [activeTheme, setActiveTheme] = useState<Theme>(() => getThemeForPreference('automatic', colorScheme));

    const applyTheme = useCallback((theme: Theme) => {
        UnistylesRuntime.setTheme(theme);
        setActiveTheme(theme);
    }, []);

    if (!UnistylesRuntime.themeName) {
        UnistylesRuntime.setTheme(activeTheme);
    }

    useEffect(() => {
        // Load saved theme preference from storage.
        const getThemePreference = async () => {
            try {
                const savedPreference = await AsyncStorage.getItem('theme');
                if (savedPreference && themePreferences.includes(savedPreference as ThemePreference)) {
                    setThemePreference(savedPreference as ThemePreference);
                }
            } catch (error) {
                console.error('Error loading theme:', error);
            } finally {
                setIsPreferenceLoaded(true);
            }
        };
        getThemePreference();
    }, []);

    useEffect(() => {
        if (isPreferenceLoaded) {
            applyTheme(getThemeForPreference(themePreference, colorScheme));
        }
    }, [colorScheme, isPreferenceLoaded, themePreference]);

    useEffect(() => {
        if (window.AndroidBridge?.setThemeColor) {
            window.AndroidBridge.setThemeColor(activeTheme, asHexColor(activeTheme === 'light' ? '#000028' : '#181A1B'));
        }
    }, [activeTheme]);

    const toggleTheme = async () => {
        const currentIndex = themePreferences.indexOf(themePreference);
        const newPreference = themePreferences[(currentIndex + 1) % themePreferences.length];
        setThemePreference(newPreference);

        // Save selected theme preference.
        await AsyncStorage.setItem('theme', newPreference);
    };

    return (
        <ThemeContext.Provider value={{ themePreference, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};
export default ThemeContext;
